import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";

const POSTERS = [
  {
    slug: "fishing",
    title: "Fishing Rules",
    subtitle: "Our lakes are for everyone — please look after them",
    rules: [
      "Anyone aged 13 or over must hold an Environment Agency rod licence — buy online at gov.uk or show us yours at check-in",
      "Day passes for pitch guests: 1 rod £6 · 2 rods £10 · 3 rods £15 · 4 rods £20 — lodge guests fish free",
      "Maximum 4 rods per person, per day",
      "Fish only from your own lodge bank or booked pitch",
      "Please handle fish with wet hands and return them gently",
    ],
    footnote: "Day passes are payable at reception or via your online check-in.",
  },
  {
    slug: "gate",
    title: "Barrier & Gate",
    subtitle: "Keeping the park safe and peaceful",
    rules: [
      "Please complete your check-in before entering — the barrier opens once you're checked in",
      "Scan the check-in QR code on this gate, or pop into reception",
      "All visitors must report to reception before entering",
      "The barrier is for guests staying with us — thank you for understanding",
    ],
    footnote: "No signal? Call 07982 844201 and we'll let you in.",
  },
  {
    slug: "quiet",
    title: "Quiet Hours",
    subtitle: "10pm — 8am, every night",
    rules: [
      "Please keep noise to a minimum between 10pm and 8am",
      "No loud music at any time — the sound carries over the lakes",
      "Late arrivals: please park considerately and settle in quietly",
      "Early departures: our thanks for leaving quietly",
    ],
    footnote: "Respect your neighbours — many guests are here for the peace and the fishing.",
  },
  {
    slug: "dogs",
    title: "Dogs on the Park",
    subtitle: "Four-legged guests are welcome",
    rules: [
      "Please keep dogs on a lead around the lakes and lodges",
      "Always clean up after your dog — bins are by reception",
      "Please don't leave dogs unattended in lodges, cars or on pitches",
      "Keep dogs back from anglers' lines and landing nets",
    ],
    footnote: "Water's edge can be slippery — keep an eye on your best friend.",
  },
  {
    slug: "checkout",
    title: "Departure Day",
    subtitle: "Check-out is 11am",
    rules: [
      "Please be ready to leave your lodge or pitch by 11am",
      "Leave your lodge as you found it — washing up done, bins emptied",
      "Hand any keys or fobs in at reception",
      "Your £100 security deposit hold releases automatically once we've checked the lodge",
    ],
    footnote: "Fancy staying longer? Ask reception about extending — subject to availability.",
  },
  {
    slug: "lakes",
    title: "Lake Safety",
    subtitle: "Deep, cold water — please take care",
    rules: [
      "No swimming or paddling in the lakes at any time",
      "No inflatables, boats or boards on the water",
      "Children must be supervised near the water at all times",
      "Banks can be steep and slippery, especially after rain",
    ],
    footnote: "In an emergency call 999, then reception on 07982 844201.",
  },
  {
    slug: "speed",
    title: "5 MPH",
    subtitle: "Dead slow, everywhere on the park",
    rules: [
      "Please drive at walking pace — 5 mph maximum",
      "Children, dogs and ducks have right of way. Always.",
      "No arrivals or departures by car after 10pm where possible",
      "Park in your own lodge or pitch space — keep the lanes clear",
    ],
    footnote: "Thank you for keeping our little lanes safe.",
  },
  {
    slug: "bbq",
    title: "BBQs & Fires",
    subtitle: "Enjoy them safely",
    rules: [
      "Raised BBQs only — nothing directly on the grass",
      "No open ground fires or fire pits anywhere on the park",
      "Never leave a lit BBQ unattended, and keep water nearby",
      "Let coals go fully cold before disposing — metal bins only",
    ],
    footnote: "One stray spark can ruin someone's holiday — thank you for being careful.",
  },
];

const PosterArt = ({ p, large }) => (
  <div
    className={`bg-white border border-[#DDD1BE] rounded-2xl text-center flex flex-col items-center ${large ? "px-10 py-12" : "px-6 py-8"}`}
    data-testid={`poster-art-${p.slug}`}
  >
    <p className="font-serif text-xl text-[#14100F] tracking-[0.22em] uppercase">Walnut Lakes</p>
    <p className="text-[9px] tracking-[0.24em] uppercase text-[#6A5F58] mt-1">Holiday Park · Lincolnshire</p>
    <div className="w-14 h-px bg-[#B76E79] my-5" />
    <h2 className="font-serif text-[#14100F] leading-tight" style={{ fontSize: large ? 44 : 30 }}>{p.title}</h2>
    <p className={`mt-2 text-[#B76E79] font-semibold ${large ? "text-base" : "text-xs"}`}>{p.subtitle}</p>
    <ul className={`mt-6 space-y-3 text-left w-full ${large ? "max-w-lg" : ""}`}>
      {p.rules.map((r, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className="font-serif text-[#B76E79] shrink-0" style={{ fontSize: large ? 20 : 16 }}>{i + 1}.</span>
          <span className={`text-[#3A322E] leading-snug ${large ? "text-[15px]" : "text-[12px]"}`}>{r}</span>
        </li>
      ))}
    </ul>
    <div className="w-14 h-px bg-[#DDD1BE] my-6" />
    <p className={`text-[#6A5F58] leading-relaxed ${large ? "text-xs" : "text-[10px]"}`}>{p.footnote}</p>
    <p className={`text-[#6A5F58] mt-2 ${large ? "text-xs" : "text-[10px]"}`}>
      Main Road, Algarkirk, Boston PE20 2LQ · 07982 844201
    </p>
  </div>
);

/**
 * Park rules posters — pre-written A4 signs to print, laminate and signpost.
 * Each card prints standalone via a print-only sheet.
 */
const AdminRulePosters = () => {
  const navigate = useNavigate();
  const [printing, setPrinting] = useState(null); // slug being printed

  const printPoster = (slug) => {
    setPrinting(slug);
    // Let the print-only sheet render before the dialog opens. The sheet stays
    // mounted afterwards — it's display:none on screen, so it's invisible and
    // harmless (and re-clicking any poster swaps the content).
    setTimeout(() => window.print(), 60);
  };

  const current = POSTERS.find((p) => p.slug === printing);

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="rule-posters-page">
      <div className="no-print flex items-center justify-between px-5 py-4 bg-[#0F0E0E]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[#F5EFE6] text-[10px] font-brand tracking-[0.16em] uppercase hover:text-[#E4B2A8]"
          data-testid="posters-back"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <p className="font-serif text-[#F5EFE6] text-sm tracking-[0.24em] uppercase">Park rules posters</p>
        <span className="w-16" />
      </div>

      <div className="no-print max-w-5xl mx-auto px-4 py-8">
        <h1 className="font-serif text-3xl text-[#14100F]">Print & signpost</h1>
        <p className="text-sm text-[#6A5F58] mt-2 max-w-2xl">
          Eight ready-made A4 rule posters for around the park — print, laminate, pin up. Want different wording
          or another rule entirely? Just ask and I'll adjust them.
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {POSTERS.map((p) => (
            <div key={p.slug} className="relative group">
              <PosterArt p={p} />
              <button
                type="button"
                onClick={() => printPoster(p.slug)}
                className="mt-3 w-full h-11 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[10px] font-brand tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2"
                data-testid={`poster-print-${p.slug}`}
              >
                <Printer size={13} /> Print this poster
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Print-only sheet — the only thing visible while printing */}
      {current && (
        <div className="rules-poster-sheet" data-testid="rules-poster-sheet">
          <PosterArt p={current} large />
        </div>
      )}
    </div>
  );
};

export default AdminRulePosters;
